import { useEffect, useRef, useState } from "react";
import { Redirect } from "react-router-dom";
import { useParams } from "react-router";

import { Box, Typography } from "@mui/material";

import { AnimatedLogo } from "@aclymatepackages/atoms";

import * as Sentry from "@sentry/react";

import Link from "../atoms/mui/Link";

import AccountAccessLayout from "./layouts/AccountAccessLayout";
import CreateAccount from "./CreateAccount";
import JoinRequest from "./JoinRequest";
import DashboardIndex from "./views";
import ExplorerEmissionsSurvey from "./emissions-survey";
import ExplorerBomSurvey from "./bom-survey";
import ExplorerScoutOnboarding from "./ExplorerScoutOnboarding";
import UpgradePage from "./views/UpgradePage";

import SignInForm from "../account-access/SignInForm";

import { handleLoggedInUser } from "../../helpers/components/accountAccess";
import { StripeCustomerContextProvider } from "../../helpers/contexts/stripeCustomer";
import { EmissionsContextProvider } from "../../helpers/contexts/emissions";
import { ExplorerLayoutContextProvider } from "../../helpers/contexts/explorerLayout";
import { getAccountId } from "../../helpers/accountHelpers";
import { getUrlSearchParams } from "../../helpers/otherHelpers";
import { useAuth, useMutateAccountData } from "../../helpers/firebase";

const Login = () => {
  const [userEmail, setUserEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);
  const [redirect, setRedirect] = useState(false);
  const [accountAccessError, setAccountAccessError] = useState("");

  const vendorId = getUrlSearchParams("vendorId");
  const role = getUrlSearchParams("role");
  const teamInvite = getUrlSearchParams("teamInvite");
  const inviteToken = getUrlSearchParams("inviteToken");
  const redirectParam = getUrlSearchParams("redirect");
  // teamInvite/inviteToken must survive this redirect -- an already-registered
  // invitee who clicks "Login instead" on the create-account page needs to
  // land back on create-account carrying its inviteToken, or
  // linkUserToVendorFromParams rejects the (now token-less) request as an
  // invalid invite.
  const postLoginUrl = redirectParam
    ? decodeURIComponent(redirectParam)
    : vendorId
      ? `/explorer/create-account?vendorId=${vendorId}${role ? `&role=${role}` : ""}${teamInvite ? `&teamInvite=${teamInvite}` : ""}${inviteToken ? `&inviteToken=${inviteToken}` : ""}`
      : "/explorer/dashboard";

  if (redirect) {
    return <Redirect to={redirect} />;
  }

  return (
    <AccountAccessLayout
      title="Welcome to Aclymate Explorer"
      error={accountAccessError}
    >
      <SignInForm
        userEmail={userEmail}
        setUserEmail={setUserEmail}
        password={password}
        setPassword={setPassword}
        loginLoading={loginLoading}
        setLoginLoading={setLoginLoading}
        setAccountAccessError={setAccountAccessError}
        onLogin={(user) =>
          handleLoggedInUser({
            user,
            setRedirect,
            setLoginLoading,
            setAccountAccessError,
            platformUrl: postLoginUrl,
            allowNoAccountId: true
          })
        }
        cta={
          <Typography variant="body1" align="center">
            First Time Here?{" "}
            <Link href="/explorer/create-account">Create an account</Link>
          </Typography>
        }
      />
    </AccountAccessLayout>
  );
};

const LoadingScreen = () => (
  <Box
    display="flex"
    alignItems="center"
    justifyContent="center"
    style={{ height: "100vh" }}
  >
    <AnimatedLogo />
  </Box>
);

const AclymateExplorerRoutes = () => {
  const { page } = useParams();
  const [accountId, setAccountId] = useState(getAccountId());
  const [user, authLoading] = useAuth();
  const [verifiedKey, setVerifiedKey] = useState("");
  const [fetchingClaims, setFetchingClaims] = useState(false);
  const mutateAccountData = useMutateAccountData();

  useEffect(() => {
    setAccountId(getAccountId());
  }, [page]);

  const resolvedAccountId = accountId || getAccountId();

  const claimsCancelledRef = useRef(false);
  useEffect(() => {
    if (!user || authLoading || resolvedAccountId) {
      return;
    }

    claimsCancelledRef.current = false;
    setFetchingClaims(true);

    user
      .getIdTokenResult(true)
      .then(({ claims }) => {
        if (claimsCancelledRef.current) return;
        if (claims.company) {
          window.sessionStorage.setItem("accountId", claims.company);
          setAccountId(claims.company);
        }
        setFetchingClaims(false);
      })
      .catch((err) => {
        if (claimsCancelledRef.current) return;
        Sentry.captureException(err, {
          tags: { feature: "explorer-auth", path: "getIdTokenResult" },
          extra: { uid: user?.uid }
        });
        setFetchingClaims(false);
      });

    return () => {
      claimsCancelledRef.current = true;
      setFetchingClaims(false);
    };
  }, [user, authLoading, resolvedAccountId]);

  const isVerified =
    !!resolvedAccountId &&
    !!user &&
    verifiedKey === `${resolvedAccountId}:${user.uid}`;

  const tokenFreshCancelledRef = useRef(false);
  useEffect(() => {
    if (
      ![
        "dashboard",
        "emissions-survey",
        "bom-survey",
        "upgrade"
      ].includes(page) ||
      !resolvedAccountId ||
      !user ||
      authLoading
    ) {
      return;
    }

    tokenFreshCancelledRef.current = false;

    const ensureTokenFresh = async () => {
      try {
        const { claims } = await user.getIdTokenResult(true);
        if (tokenFreshCancelledRef.current) return;
        if (claims.company === resolvedAccountId) {
          setVerifiedKey(`${resolvedAccountId}:${user.uid}`);
          return;
        }
        // Same invalidation as logout: without this, a session that
        // re-links to this same accountId later in the same tab would be
        // served this now-untrustworthy doc from the stale cache slot.
        mutateAccountData(resolvedAccountId);
        window.sessionStorage.removeItem("accountId");
        setAccountId("");
      } catch (err) {
        if (tokenFreshCancelledRef.current) return;
        Sentry.captureException(err, {
          tags: { feature: "explorer-auth", path: "ensureTokenFresh" },
          extra: { uid: user?.uid, resolvedAccountId, page }
        });
      }
    };

    ensureTokenFresh();

    return () => {
      tokenFreshCancelledRef.current = true;
    };
  }, [page, accountId, user, authLoading, resolvedAccountId, mutateAccountData]);

  if (page === "create-account") {
    return <CreateAccount />;
  }

  if (page === "join-request") {
    return <JoinRequest />;
  }

  if (page === "add-product") {
    return <Redirect to="/explorer/dashboard" />;
  }

  if (!page) {
    if (authLoading || fetchingClaims) {
      return <LoadingScreen />;
    }
    if (user && resolvedAccountId) {
      return <Redirect to="/explorer/dashboard" />;
    }
    return <Login />;
  }

  if (authLoading || fetchingClaims) {
    return <LoadingScreen />;
  }

  if (!user) {
    return <Redirect to="/explorer" />;
  }

  if (resolvedAccountId && !isVerified) {
    return <LoadingScreen />;
  }

  if (!resolvedAccountId) {
    return (
      <ExplorerScoutOnboarding
        mode="self_service"
        onCompanyLinked={setAccountId}
      />
    );
  }

  if (page === "emissions-survey") {
    return <ExplorerEmissionsSurvey />;
  }

  if (page === "bom-survey") {
    return <ExplorerBomSurvey />;
  }

  if (page === "upgrade") {
    return <UpgradePage />;
  }

  return (
    <StripeCustomerContextProvider>
      <EmissionsContextProvider>
        <DashboardIndex />
      </EmissionsContextProvider>
    </StripeCustomerContextProvider>
  );
};

const AclymateExplorer = () => (
  <ExplorerLayoutContextProvider>
    <AclymateExplorerRoutes />
  </ExplorerLayoutContextProvider>
);
export default AclymateExplorer;
